// 必须放在所有 import 之前：旧 Android WebView 缺失的 API 兜底（react-markdown / mermaid 等依赖）。
import '../polyfills'
import React, { useRef, useMemo, useEffect } from 'react'
import BaseLayout from '@ssr/components/BaseLayout'
import { CommonContext } from '@ssr/context'
// import { PostMessage } from '@ssr/lib/webview_invoke_app/common'
import { sendException } from '@ssr/utils'

import './styles.less'
import Head from 'next/head'
import { ToastProvider } from '@ssr/components/Toast'

// L3: 共享的忽略键集合，避免 React / 序列化 / DevTools 等运行时探测路径被误判为缺失 key
const I18N_IGNORED_KEYS = ['inspect', 'toJSON', 'constructor', 'then', 'nodeType', 'tagName', 'length', '$$typeof', 'asymmetricMatch']
const I18N_IGNORED_KEY_SET = new Set(I18N_IGNORED_KEYS)
const shouldIgnoreLocaleKey = (k) =>
  typeof k !== 'string' || I18N_IGNORED_KEY_SET.has(k) || k.startsWith('__') || k.startsWith('@@')

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true }
  }

  componentDidCatch(error) {
    sendException(
      {
        name: 'WhiteScreenError',
        message: `白屏, ${error.message}`,
      },
      {
        fingerprint: ['WhiteScreenError', error.message],
      }
    )
  }
  render() {
    if (this.state.hasError) {
      return <div style={{ width: '100vw', height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>System Error, please try again later</div>
    }
    return this.props.children
  }
}

const App = (props) => {
  const reportedRef = useRef(new Set())

  // L3: 用 Proxy 包装 localeData，拦截 undefined key 访问
  const proxiedLocaleData = useMemo(() => {
    if (!props.localeData) return {}
    return new Proxy(props.localeData, {
      get(target, key) {
        if (!shouldIgnoreLocaleKey(key) && !(key in target)) {
          if (!reportedRef.current.has(key)) {
            reportedRef.current.add(key)
            sendException({
              name: 'i18n_client_key_missing',
              message: `CommonContext key缺失: ${key} locale: ${props.locale}`,
              data: { key, locale: props.locale },
            }, { fingerprint: ['i18n', 'client_missing'], tags: { 'insta:mute': true } })
          }
        }
        return target[key]
      },
    })
  }, [props.localeData])

  // L3: 上报 window.__ Proxy 收集到的缺失 key
  useEffect(() => {
    if (typeof window !== 'undefined' && window.__i18nMissing && window.__i18nMissing.length > 0) {
      const keys = [...new Set(window.__i18nMissing)]
      keys.forEach((key) => {
        sendException({
          name: 'i18n_client_key_missing',
          message: `window.__ key缺失: ${key} locale: ${props.locale}`,
          data: { key, locale: props.locale },
        }, { fingerprint: ['i18n', 'client_missing'], tags: { 'insta:mute': true } })
      })
      delete window.__i18nMissing
    }
  }, [])

  if (props.skipRender) return null
  const { Component, pageProps } = props

  // useEffect(() => {
  //   if (getToken() && !getUserRegion()) {
  //     const invoker = new WebviewInvokeApp()
  //     invoker.enterLoginPage()
  //   }
  // }, [])

  return (
    <ErrorBoundary>
      <Head>
        <script
          id='page-env-data'
          dangerouslySetInnerHTML={{
            __html: [
              `window.SENTRY_ENVIRONMENT = '${props.SENTRY_ENVIRONMENT}'`,
              `window.SENTRY_DSN = '${props.SENTRY_DSN}'`,
              `window.NEXT_BUILD_ID = '${props.NEXT_BUILD_ID}'`,
              `window.SERVICE_DOMAIN_EXTERNAL_CN = '${props.SERVICE_DOMAIN_EXTERNAL_CN}'`,
              `window.SERVICE_DOMAIN_EXTERNAL_GLOBAL = '${props.SERVICE_DOMAIN_EXTERNAL_GLOBAL}'`,
              `window.SERVICE_DOMAIN_CLOUD_EXTERNAL_CN = '${props.SERVICE_DOMAIN_CLOUD_EXTERNAL_CN}'`,
              `window.SERVICE_DOMAIN_CLOUD_EXTERNAL_GLOBAL = '${props.SERVICE_DOMAIN_CLOUD_EXTERNAL_GLOBAL}'`,
              `window.env = '${props.env}'`,
              `window.isOversea = ${props.isOversea}`,
              `window.locale = '${props.locale}'`,
              `window.isDev = ${props.metaData.isDev}`,
              `window.isCN = ${props.isCN}`,
              `window.isMobile = true`,
              props.localeSideEffect
                ? `(function(){var d=${JSON.stringify(props.localeData || {})};var IG=new Set(${JSON.stringify(I18N_IGNORED_KEYS)});var m=[];window.__i18nMissing=m;window.__=new Proxy(d,{get:function(t,k){if(typeof k==='string'&&!(k in t)&&!IG.has(k)&&k.indexOf('__')!==0&&k.indexOf('@@')!==0){m.push(k)}return t[k]}})})()`
                : '',
              `window.version = '${props.version}'`,
              `window.ab_campaign_name = '${props.metaData.ab_campaign_name || ''}'`,
              `window.ab_variation_name = '${props.metaData.ab_variation_name || ''}'`,
              `window.__FEATURE_SWITCHES__ = ${JSON.stringify(props.metaData.feature_switches || {}).replace(/</g, '\\u003c')}`,
            ].join(';'),
          }}
        />
      </Head>
      <CommonContext.Provider
        value={{
          __: proxiedLocaleData,
          locale: props.locale,
          isDev: props.metaData.isDev,
          isCN: props.isCN,
          isOversea: props.isOversea,
          metaData: props.metaData,
        }}>
          <ToastProvider>
            <BaseLayout>
              <Component {...pageProps} />
            </BaseLayout>
          </ToastProvider>
      </CommonContext.Provider>
    </ErrorBoundary>
  )
}

// 获取页面注入注入数据
async function getInjectConfig(Component, res) {
  let injectConfig = {}
  injectConfig = await Component.injectData(res)
  return injectConfig
}

App.getInitialProps = async (data) => {
  if (!data?.ctx?.res) return { skipRender: true }

  // 如果route='/_error'，则表示是nextHandler没有找到对应的文件，此处跳过后续的逻辑，防止报500错误
  if (data.router.route === '/_error') return { skipRender: true }

  const { res, req } = data.ctx
  // 页面公共内容注入
  const injectConfig = await getInjectConfig(data.Component, res)

  const { sideEffect: localeSideEffect = true, ...localeConfig } = injectConfig.localeData

  // 获取页面多语言数据
  const localeData = res.getLocaleData?.({ ...localeConfig })

  // Analytics
  const openAnalytics = injectConfig.openAnalytics ?? true

  const { env, locale, isOversea, isDev, isCN, page_title, page_description, page_keywords, version, ab_campaign_name = '', ab_variation_name = '', feature_switches = {} } = res.locals || {}
  const SERVICE_DOMAIN_EXTERNAL_CN = process.env.SERVICE_DOMAIN_EXTERNAL_CN
  const SERVICE_DOMAIN_EXTERNAL_GLOBAL = process.env.SERVICE_DOMAIN_EXTERNAL_GLOBAL
  const SERVICE_DOMAIN_CLOUD_EXTERNAL_CN = process.env.SERVICE_DOMAIN_CLOUD_EXTERNAL_CN
  const SERVICE_DOMAIN_CLOUD_EXTERNAL_GLOBAL = process.env.SERVICE_DOMAIN_CLOUD_EXTERNAL_GLOBAL
  const metaData = {
    isDev,
    page_title,
    page_description,
    ab_campaign_name,
    ab_variation_name,
    feature_switches,
    page_keywords,
    openAnalytics,
    app_version: req.cookies.insta_app_version,
    SERVICE_DOMAIN_EXTERNAL_CN,
    SERVICE_DOMAIN_EXTERNAL_GLOBAL,
    SERVICE_DOMAIN_CLOUD_EXTERNAL_CN,
    SERVICE_DOMAIN_CLOUD_EXTERNAL_GLOBAL,
  }

  const SENTRY_ENVIRONMENT = process.env.SENTRY_ENVIRONMENT
  const SENTRY_DSN = process.env.SENTRY_DSN
  const NEXT_BUILD_ID = process.env.NEXT_BUILD_ID

  return {
    metaData,
    localeData,
    locale,
    localeSideEffect,
    isOversea,
    env,
    isCN,
    version,
    SENTRY_ENVIRONMENT,
    SENTRY_DSN,
    NEXT_BUILD_ID,
    SERVICE_DOMAIN_EXTERNAL_CN,
    SERVICE_DOMAIN_EXTERNAL_GLOBAL,
    SERVICE_DOMAIN_CLOUD_EXTERNAL_CN,
    SERVICE_DOMAIN_CLOUD_EXTERNAL_GLOBAL,
  }
}

export default App
